import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import testimonials from '../../data/OurUser.js';

const ImageModal = ({ image, name, onClose }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
        onClick={onClose}
    >
        <div className="relative max-w-3xl w-full mx-4">
            <button
                className="absolute -top-10 right-0 text-white text-3xl hover:text-gray-300"
                onClick={onClose}
            >
                ×
            </button>
            <motion.img
                src={image}
                alt={name}
                className="w-full h-auto rounded-lg"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                onClick={(e) => e.stopPropagation()}
            />
        </div>
    </motion.div>
);

const achievements = [
    {
        count: "500+",
        label: "JEE Selections",
        icon: "🎓"
    },
    {
        count: "1000+",
        label: "12th Success Stories",
        icon: "📚"
    },
    {
        count: "95%",
        label: "Success Rate",
        icon: "📈"
    },
    {
        count: "6+",
        label: "Years Experience",
        icon: "⭐"
    }
];

const OurUser = () => {
    const [selectedImage, setSelectedImage] = useState(null);

    const TestimonialCard = ({ testimonial }) => (
        <motion.div
            className="min-w-[350px] bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
            whileHover={{ scale: 1.05 }}
        >
            <div
                className="relative h-48 cursor-pointer"
                onClick={() => setSelectedImage(testimonial)}
            >
                <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <h3 className="text-white font-bold text-xl">{testimonial.name}</h3>
                    <p className="text-white/90">{testimonial.course}</p>
                </div>
            </div>
            <div className="p-6">
                <div className="mb-4 flex items-center justify-between">
                    {testimonial.rank && (
                        <div className="bg-green-100 px-4 py-1 rounded-full">
                            <p className="text-green-600 font-semibold">Rank: {testimonial.rank}</p>
                        </div>
                    )}
                    {testimonial.percentage && (
                        <div className="bg-blue-100 px-4 py-1 rounded-full">
                            <p className="text-blue-600 font-semibold">Score: {testimonial.percentage}</p>
                        </div>
                    )}
                </div>
                <p className="text-gray-600 italic leading-relaxed">"{testimonial.testimonial}"</p>
            </div>
        </motion.div>
    );

    return (
        <div className="bg-gradient-to-b from-blue-50 to-white py-16">
            <div className="container mx-auto px-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Success Stories</h2>
                    <p className="text-lg text-gray-600">Transforming dreams into reality through expert guidance</p>
                </motion.div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
                    {achievements.map((item, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.5 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="bg-white rounded-lg p-6 shadow-lg text-center"
                        >
                            <div className="text-4xl mb-2">{item.icon}</div>
                            <div className="text-3xl font-bold text-blue-600">{item.count}</div>
                            <div className="text-gray-600">{item.label}</div>
                        </motion.div>
                    ))}
                </div>

                <div className="relative overflow-hidden py-8">
                    <motion.div
                        className="flex gap-6"
                        animate={{
                            x: [-1000, 0],
                        }}
                        transition={{
                            duration: 20,
                            repeat: Infinity,
                            ease: "linear"
                        }}
                    >
                        {testimonials.map((testimonial) => (
                            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
                        ))}
                        {testimonials.map((testimonial) => (
                            <TestimonialCard key={`duplicate-${testimonial.id}`} testimonial={testimonial} />
                        ))}
                    </motion.div>

                    <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-blue-50 to-transparent z-10"></div>
                    <div className="absolute top-0 right-0 h-full w-20 bg-gradient-to-l from-blue-50 to-transparent z-10"></div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mt-16"
                >
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">Ready to Start Your Success Journey?</h3>
                    <button className="bg-blue-600 text-white px-8 py-3 rounded-full hover:bg-blue-700 transition-colors">
                        Book Free Counseling Session
                    </button>
                </motion.div>
            </div>

            <AnimatePresence>
                {selectedImage && (
                    <ImageModal
                        image={selectedImage.image}
                        name={selectedImage.name}
                        onClose={() => setSelectedImage(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default OurUser;
